/**
 * Topics / Departments Workspaces page — /workspaces/topics
 *
 * Shows all themed/custom workspaces organised as a
 * State → Topic/Department → Workspace tree with a slide-out detail panel.
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import {
  ScopeSelector,
  type ScopeSelection,
} from '@/components/tools/ScopeSelector';
import type { Workspace, Jurisdiction } from '@/types/database';
import type { WorkspaceCollaborator } from '@/types/database';
import {
  ChevronRight,
  Plus,
  X,
  ExternalLink,
  Globe,
  Calendar,
  Tag,
  Hash,
  Loader2,
  Users,
  Layers,
} from 'lucide-react';
import { apiFetch } from '@/lib/apiFetch';

// ── Helpers ───────────────────────────────────────────────────────────────────

const ACCENT: Record<
  string,
  { bg: string; text: string; ring: string; dot: string }
> = {
  blue: {
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    ring: 'ring-blue-200',
    dot: 'bg-blue-500',
  },
  violet: {
    bg: 'bg-violet-50',
    text: 'text-violet-700',
    ring: 'ring-violet-200',
    dot: 'bg-violet-500',
  },
  teal: {
    bg: 'bg-teal-50',
    text: 'text-teal-700',
    ring: 'ring-teal-200',
    dot: 'bg-teal-500',
  },
  amber: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    ring: 'ring-amber-200',
    dot: 'bg-amber-500',
  },
  slate: {
    bg: 'bg-slate-100',
    text: 'text-slate-600',
    ring: 'ring-slate-200',
    dot: 'bg-slate-400',
  },
};

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    active: {
      label: 'Active',
      cls: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    },
    inactive: {
      label: 'Inactive',
      cls: 'bg-gray-100 text-gray-500 border-gray-200',
    },
    draft: {
      label: 'Draft',
      cls: 'bg-amber-100 text-amber-700 border-amber-200',
    },
    archived: {
      label: 'Archived',
      cls: 'bg-slate-100 text-slate-500 border-slate-200',
    },
  };
  const cfg = map[status] ?? {
    label: status,
    cls: 'bg-gray-100 text-gray-500 border-gray-200',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${cfg.cls}`}
    >
      {cfg.label}
    </span>
  );
}

function TagPill({ label, color }: { label: string; color: string }) {
  const c = ACCENT[color] ?? ACCENT.slate;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${c.bg} ${c.text} ${c.ring}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} />
      {label}
    </span>
  );
}

// ── Topic tree ─────────────────────────────────────────────────────────────────

type JurMap = Map<string, Jurisdiction>;
type ThemeMap = Map<string, string>;

interface TopicGroup {
  topicId: string;
  topicName: string;
  workspaces: Workspace[];
}
interface TopicStateGroup {
  stateId: string;
  stateName: string;
  topics: TopicGroup[];
  unorganized: Workspace[];
}

function resolveState(
  jurisdictionId: string,
  jmap: JurMap
): Jurisdiction | null {
  let cur: Jurisdiction | null = jmap.get(jurisdictionId) ?? null;
  while (cur) {
    if (cur.level === 'state') return cur;
    cur = cur.parent_id ? (jmap.get(cur.parent_id) ?? null) : null;
  }
  return null;
}

function buildTopicTree(
  workspaces: Workspace[],
  themeNames: ThemeMap,
  orgThemeNames: ThemeMap,
  jmap: JurMap
): TopicStateGroup[] {
  const stateMap = new Map<string, TopicStateGroup>();

  const getOrCreateState = (stateId: string, stateName: string) => {
    if (!stateMap.has(stateId))
      stateMap.set(stateId, {
        stateId,
        stateName,
        topics: [],
        unorganized: [],
      });
    return stateMap.get(stateId)!;
  };

  const getOrCreateTopic = (
    sg: TopicStateGroup,
    topicId: string,
    topicName: string
  ) => {
    let tg = sg.topics.find((t) => t.topicId === topicId);
    if (!tg) {
      tg = { topicId, topicName, workspaces: [] };
      sg.topics.push(tg);
    }
    return tg;
  };

  for (const ws of workspaces) {
    let topicId: string;
    let topicName: string;
    if (ws.theme_id && themeNames.has(ws.theme_id)) {
      topicId = ws.theme_id;
      topicName = themeNames.get(ws.theme_id)!;
    } else if (ws.org_theme_id && orgThemeNames.has(ws.org_theme_id)) {
      topicId = ws.org_theme_id;
      topicName = orgThemeNames.get(ws.org_theme_id)!;
    } else {
      topicId = '__custom__';
      topicName = 'Custom';
    }

    if (ws.jurisdiction_id) {
      const state = resolveState(ws.jurisdiction_id, jmap);
      const stateId = state?.id ?? '__unknown__';
      const stateName = state?.name ?? 'Unknown';
      getOrCreateTopic(
        getOrCreateState(stateId, stateName),
        topicId,
        topicName
      ).workspaces.push(ws);
    } else {
      getOrCreateTopic(
        getOrCreateState('__all__', 'All States'),
        topicId,
        topicName
      ).workspaces.push(ws);
    }
  }

  const states = Array.from(stateMap.values()).sort((a, b) =>
    a.stateName.localeCompare(b.stateName)
  );
  for (const sg of states)
    sg.topics.sort((a, b) => a.topicName.localeCompare(b.topicName));
  return states;
}

// ── Slide-out panel ───────────────────────────────────────────────────────────

function SlideOut({
  workspace,
  onClose,
  onNavigate,
}: {
  workspace: Workspace | null;
  onClose: () => void;
  onNavigate: (id: string) => void;
}) {
  const [members, setMembers] = useState<WorkspaceCollaborator[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    if (workspace) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [workspace, onClose]);

  useEffect(() => {
    let cancelled = false;
    const wsId = workspace?.id ?? null;
    async function load() {
      if (!wsId) {
        if (!cancelled) setMembers([]);
        return;
      }
      if (!cancelled) setLoadingMembers(true);
      try {
        const r = await apiFetch(`/api/workspaces/${wsId}/collaborators`);
        const data: WorkspaceCollaborator[] = r.ok ? await r.json() : [];
        if (!cancelled) setMembers(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setMembers([]);
      } finally {
        if (!cancelled) setLoadingMembers(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [workspace?.id]);

  const fmt = (s: string | null | undefined) =>
    s
      ? new Date(s).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : '—';

  const isVisible = !!workspace;

  return (
    <>
      <div
        className={`fixed inset-0 z-30 bg-black/20 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
      />
      <div
        ref={ref}
        className={`fixed right-0 top-0 z-40 flex h-full w-[440px] flex-col bg-white shadow-2xl transition-transform duration-300 ease-in-out ${isVisible ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {workspace && (
          <>
            <div className="flex items-start justify-between border-b border-gray-100 px-6 py-5">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-violet-100">
                  <Layers className="h-5 w-5 text-violet-600" />
                </div>
                <div className="min-w-0">
                  <p className="truncate text-base font-semibold text-gray-900">
                    {workspace.name}
                  </p>
                  <p className="text-xs text-gray-400">
                    {workspace.scope} · {workspace.kind}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="ml-4 mt-0.5 shrink-0 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
              <div className="flex flex-wrap gap-2">
                <TagPill
                  label={workspace.scope === 'platform' ? 'Platform' : 'Org'}
                  color={workspace.scope === 'platform' ? 'blue' : 'teal'}
                />
                <TagPill
                  label={
                    workspace.kind.charAt(0).toUpperCase() +
                    workspace.kind.slice(1)
                  }
                  color={workspace.kind === 'themed' ? 'violet' : 'amber'}
                />
                {workspace.published_to_org && (
                  <TagPill label="Org-wide" color="teal" />
                )}
                <StatusBadge status={workspace.status} />
              </div>

              {workspace.description && (
                <p className="text-sm text-gray-600 leading-relaxed">
                  {workspace.description}
                </p>
              )}

              <div className="rounded-xl border border-gray-100 bg-gray-50 divide-y divide-gray-100">
                {[
                  {
                    icon: Hash,
                    label: 'Workspace ID',
                    value: (
                      <span className="font-mono text-xs text-gray-400 break-all">
                        {workspace.id}
                      </span>
                    ),
                  },
                  {
                    icon: Calendar,
                    label: 'Created',
                    value: fmt(workspace.created_at),
                  },
                  {
                    icon: Calendar,
                    label: 'Last updated',
                    value: fmt(workspace.updated_at),
                  },
                  ...(workspace.jurisdiction_id
                    ? [
                        {
                          icon: Globe,
                          label: 'Jurisdiction ID',
                          value: (
                            <span className="font-mono text-xs text-gray-400 break-all">
                              {workspace.jurisdiction_id}
                            </span>
                          ),
                        },
                      ]
                    : []),
                  ...(workspace.date_from || workspace.date_to
                    ? [
                        {
                          icon: Calendar,
                          label: 'Date range',
                          value: `${fmt(workspace.date_from)} → ${fmt(workspace.date_to)}`,
                        },
                      ]
                    : []),
                  ...(workspace.created_via
                    ? [
                        {
                          icon: Tag,
                          label: 'Created via',
                          value: workspace.created_via,
                        },
                      ]
                    : []),
                  {
                    icon: Tag,
                    label: 'Include descendants',
                    value: workspace.include_descendants ? 'Yes' : 'No',
                  },
                ].map(({ icon: Icon, label, value }) => (
                  <div key={label} className="flex items-start gap-3 px-4 py-3">
                    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                        {label}
                      </p>
                      <div className="mt-0.5 text-sm text-gray-700">
                        {value}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <div className="mb-2 flex items-center gap-2">
                  <Users className="h-4 w-4 text-gray-400" />
                  <p className="text-sm font-semibold text-gray-700">
                    Collaborators
                  </p>
                </div>
                {loadingMembers ? (
                  <div className="flex items-center gap-2 py-3 text-sm text-gray-400">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                  </div>
                ) : members.length === 0 ? (
                  <p className="text-sm text-gray-400">No collaborators yet.</p>
                ) : (
                  <div className="space-y-2">
                    {members.map((m) => {
                      const name = m.user
                        ? `${m.user.first_name ?? ''} ${m.user.last_name ?? ''}`.trim() ||
                          m.user.email
                        : m.user_id;
                      return (
                        <div
                          key={m.id}
                          className="flex items-center justify-between rounded-lg border border-gray-100 bg-white px-3 py-2"
                        >
                          <div className="flex items-center gap-2">
                            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-violet-100 text-xs font-semibold uppercase text-violet-700">
                              {m.user?.first_name?.[0] ??
                                m.user?.email?.[0] ??
                                '?'}
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-800">
                                {name}
                              </p>
                              {m.user?.email && (
                                <p className="text-xs text-gray-400">
                                  {m.user.email}
                                </p>
                              )}
                            </div>
                          </div>
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
                            {m.can_edit ? 'Editor' : 'Viewer'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-gray-100 px-6 py-4">
              <Button
                className="w-full bg-violet-600 hover:bg-violet-700 text-white"
                onClick={() => onNavigate(workspace.id)}
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Open Full Detail
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ── Workspace row ─────────────────────────────────────────────────────────────

function WorkspaceRow({
  ws,
  isSelected,
  onSelect,
  indent = 0,
}: {
  ws: Workspace;
  isSelected: boolean;
  onSelect: (ws: Workspace) => void;
  indent?: number;
}) {
  const indentClass = indent === 0 ? 'pl-5' : indent === 1 ? 'pl-8' : 'pl-14';
  return (
    <button
      type="button"
      onClick={() => onSelect(ws)}
      className={`grid w-full grid-cols-[1fr_auto_auto] items-center gap-x-4 py-2.5 pr-5 text-left transition-colors border-b border-gray-50 last:border-0
        ${indentClass} ${isSelected ? 'bg-violet-50' : 'hover:bg-slate-50'}`}
    >
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-gray-800">{ws.name}</p>
        <p className="mt-0.5 font-mono text-[10px] text-gray-300 truncate">
          {ws.id} ·{' '}
          {new Date(ws.created_at).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </p>
      </div>
      <div className="flex items-center gap-1.5">
        {ws.scope === 'platform' && <TagPill label="Platform" color="blue" />}
        {ws.kind === 'themed' && <TagPill label="Themed" color="violet" />}
        {ws.published_to_org && <TagPill label="Org-wide" color="amber" />}
      </div>
      <StatusBadge status={ws.status} />
    </button>
  );
}

// ── Tree nodes ────────────────────────────────────────────────────────────────

function TopicNode({
  tg,
  selectedId,
  onSelect,
}: {
  tg: TopicGroup;
  selectedId: string | null;
  onSelect: (ws: Workspace) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 py-2 pl-8 pr-4 text-left hover:bg-gray-50 transition-colors border-b border-gray-50"
      >
        <ChevronRight
          className={`h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform duration-150 ${open ? 'rotate-90' : ''}`}
        />
        <Layers className="h-3.5 w-3.5 shrink-0 text-violet-500" />
        <span className="flex-1 text-sm font-medium text-gray-700">
          {tg.topicName}
        </span>
        <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-600">
          {tg.workspaces.length}
        </span>
      </button>
      {open &&
        tg.workspaces.map((ws) => (
          <WorkspaceRow
            key={ws.id}
            ws={ws}
            isSelected={selectedId === ws.id}
            onSelect={onSelect}
            indent={2}
          />
        ))}
    </div>
  );
}

function TopicStateNode({
  sg,
  selectedId,
  onSelect,
  defaultOpen,
}: {
  sg: TopicStateGroup;
  selectedId: string | null;
  onSelect: (ws: Workspace) => void;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  const total =
    sg.topics.reduce((n, t) => n + t.workspaces.length, 0) +
    sg.unorganized.length;

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 px-4 py-3.5 text-left hover:bg-gray-50 transition-colors"
      >
        <ChevronRight
          className={`h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
        />
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-50">
          <Globe className="h-4 w-4 text-violet-600" />
        </div>
        <p className="flex-1 text-sm font-semibold text-gray-800">
          {sg.stateName}
        </p>
        <span className="shrink-0 rounded-full bg-violet-50 px-2.5 py-0.5 text-xs font-semibold text-violet-700 ring-1 ring-inset ring-violet-200">
          {total}
        </span>
      </button>

      <div
        className={`overflow-hidden transition-all duration-200 ease-in-out ${
          open ? 'max-h-[9999px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="border-t border-gray-100">
          {sg.topics.map((tg) => (
            <TopicNode
              key={tg.topicId}
              tg={tg}
              selectedId={selectedId}
              onSelect={onSelect}
            />
          ))}
          {sg.unorganized.map((ws) => (
            <WorkspaceRow
              key={ws.id}
              ws={ws}
              isSelected={selectedId === ws.id}
              onSelect={onSelect}
              indent={1}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 py-20 text-center">
      <Layers className="mb-4 h-12 w-12 text-gray-300" />
      <h3 className="mb-1 text-base font-semibold text-gray-700">
        No topic workspaces yet
      </h3>
      <p className="mb-6 text-sm text-gray-400">
        Create a themed workspace to organise by topic or department.
      </p>
      <Button
        onClick={onCreate}
        className="bg-violet-600 hover:bg-violet-700 text-white"
      >
        <Plus className="mr-2 h-4 w-4" />
        New Workspace
      </Button>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export const WorkspaceTopicsPage: React.FC = () => {
  usePageTitle('Topics / Departments');
  const navigate = useNavigate();
  const {
    workspaces,
    loading,
    error: workspacesError,
    fetchWorkspaces,
    setCurrentWorkspace,
    createWorkspace,
  } = useWorkspaces();

  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => void fetchWorkspaces(), 12000);
    return () => clearTimeout(t);
  }, [loading, fetchWorkspaces]);

  // Jurisdiction map for state resolution
  const [jmap, setJmap] = useState<JurMap>(new Map());
  useEffect(() => {
    apiFetch('/api/jurisdictions')
      .then((r) => (r.ok ? r.json() : null))
      .then((res) => {
        const list: Jurisdiction[] = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
            ? res.data
            : [];
        setJmap(new Map(list.map((j: Jurisdiction) => [j.id, j])));
      })
      .catch(() => {});
  }, []);

  // Theme name maps
  const [themeNames, setThemeNames] = useState<ThemeMap>(new Map());
  const [orgThemeNames, setOrgThemeNames] = useState<ThemeMap>(new Map());
  useEffect(() => {
    apiFetch('/api/themes')
      .then((r) => (r.ok ? r.json() : null))
      .then((res) => {
        const list: { id: string; name: string }[] = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
            ? res.data
            : [];
        setThemeNames(new Map(list.map((t) => [t.id, t.name])));
      })
      .catch(() => {});
    apiFetch('/api/org-themes')
      .then((r) => (r.ok ? r.json() : null))
      .then((res) => {
        const list: { id: string; name: string }[] = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
            ? res.data
            : [];
        setOrgThemeNames(new Map(list.map((t) => [t.id, t.name])));
      })
      .catch(() => {});
  }, []);

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createScope, setCreateScope] = useState<ScopeSelection>({});
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [selectedWs, setSelectedWs] = useState<Workspace | null>(null);

  const topicWorkspaces = useMemo(
    () =>
      workspaces.filter((ws) => ws.kind === 'themed' || ws.kind === 'custom'),
    [workspaces]
  );

  const topicTree = useMemo(
    () => buildTopicTree(topicWorkspaces, themeNames, orgThemeNames, jmap),
    [topicWorkspaces, themeNames, orgThemeNames, jmap]
  );

  const openCreate = () => {
    setCreateName('');
    setCreateScope({});
    setCreateError(null);
    setCreateOpen(true);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createName.trim()) return;
    setCreateLoading(true);
    setCreateError(null);
    try {
      const workspace = await createWorkspace({
        name: createName.trim(),
        kind: 'custom',
        jurisdiction_id: createScope.jurisdictionId ?? null,
        theme_id: createScope.themeId ?? null,
        org_theme_id: createScope.orgThemeId ?? null,
        date_from: createScope.dateFrom ?? null,
        date_to: createScope.dateTo ?? null,
        include_descendants: true,
      });
      setCreateOpen(false);
      navigate(`/workspaces/${workspace.id}`);
    } catch (err) {
      setCreateError(
        err instanceof Error ? err.message : 'Failed to create workspace'
      );
    } finally {
      setCreateLoading(false);
    }
  };

  const handleNavigate = (id: string) => {
    const ws = workspaces.find((w) => w.id === id);
    if (ws) setCurrentWorkspace(ws);
    setSelectedWs(null);
    navigate(`/workspaces/${id}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-violet-600 border-t-transparent" />
          <p className="text-sm text-gray-500">Loading workspaces…</p>
        </div>
      </div>
    );
  }

  if (workspacesError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-6 py-5 max-w-md">
          <p className="mb-1 text-sm font-semibold text-red-700">
            Failed to load workspaces
          </p>
          <p className="text-xs text-red-500">{workspacesError}</p>
        </div>
        <Button
          onClick={() => void fetchWorkspaces()}
          className="bg-violet-600 hover:bg-violet-700 text-white"
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Topics / Departments
          </h1>
          <p className="mt-0.5 text-sm text-gray-500">
            Workspaces organised by State and Topic / Department
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-violet-600 hover:bg-violet-700 text-white"
        >
          <Plus className="mr-2 h-4 w-4" />
          New Workspace
        </Button>
      </div>

      {/* Tree */}
      {topicWorkspaces.length === 0 ? (
        <EmptyState onCreate={openCreate} />
      ) : (
        <div className="space-y-3">
          {topicTree.map((sg, i) => (
            <TopicStateNode
              key={sg.stateId}
              sg={sg}
              selectedId={selectedWs?.id ?? null}
              onSelect={setSelectedWs}
              defaultOpen={i === 0}
            />
          ))}
        </div>
      )}

      {/* Slide-out */}
      <SlideOut
        workspace={selectedWs}
        onClose={() => setSelectedWs(null)}
        onNavigate={handleNavigate}
      />

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-lg bg-white text-gray-900 border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-gray-900">New Workspace</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label htmlFor="create-name" className="text-gray-700">
                Name
              </Label>
              <Input
                id="create-name"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="e.g. Land Use — Planning Dept."
                required
                autoFocus
                className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-gray-700">Scope (optional)</Label>
              <ScopeSelector
                value={createScope}
                onChange={setCreateScope}
                showDateRange
              />
            </div>
            {createError && (
              <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {createError}
              </p>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreateOpen(false)}
                className="border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createLoading || !createName.trim()}
                className="bg-violet-600 hover:bg-violet-700 text-white"
              >
                {createLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating…
                  </>
                ) : (
                  'Create Workspace'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
